import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { createGlobalStyle } from "styled-components";

import { setDarkMode } from "@/components/CloneProgress/state/cloneProgressReducer";
import type { MondayBoardContext, MondayService } from "@/services/monday-service";
import { ThemeEnum } from "@/services/monday-service";
import { Theme } from "@/types/common";

import LoadingPage from "../../components/LoadingPage";
import RestrictedAccess from "../../components/RestrictedAccess";
import { getErrorMessage } from "../../lib/error";

const CurrentMondayContext = createContext<MondayBoardContext | null>(null);

const initialContext = {
  theme: ThemeEnum.LIGHT,
} as MondayBoardContext;

interface Props {
  children: React.ReactNode;
  mondayService: MondayService;
}

const BodyGlobalStyle = createGlobalStyle`
  body {
    color: var(--primary-text-color);
    font-family: var(--font-family);
  }

  .monday-style-dialog-content-wrapper {
    z-index:2;
  }
`;

// This component makes the Monday context available to its child components.
// Child components MUST use method `useCurrentMondayContext` to access it:
// This avoids that every components calls the same Monday API methods without knowing other
// components already did the same.
export default function WithMondayContextAvailable({ children, mondayService }: Props) {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isViewOnly, setViewOnly] = useState(false);
  const [currentContext, setCurrentContext] = useState<MondayBoardContext>(initialContext);
  const dispatch = useDispatch();

  useEffect(() => {
    void (async () => {
      try {
        const context = await mondayService.getBoardContext();
        if (context.user?.isViewOnly || context.user?.isGuest) {
          setViewOnly(true);
          return;
        }
        setCurrentContext({ ...context });
      } catch (error) {
        setErrorMessage(getErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    })()
      .then(() => {})
      .catch(() => {});
  }, [mondayService]);

  useLayoutEffect(() => {
    let curTheme = "";
    mondayService.listenToMondayContext((data: MondayBoardContext) => {
      const newTheme = `${data.theme}-app-theme`;
      if (newTheme === curTheme) {
        return;
      }

      if (!curTheme) {
        document.body.classList.add(newTheme);
      } else {
        document.body.classList.replace(curTheme, newTheme);
      }
      curTheme = newTheme;
      setCurrentContext((cur) => ({ ...cur, theme: data.theme }));
      dispatch(setDarkMode(`${data.theme}` === `${Theme.DARK}`));
    });
  }, []);

  if (isLoading) {
    return <LoadingPage label="Loading context..." />;
  }

  if (isViewOnly) {
    return <RestrictedAccess />;
  }

  if (!currentContext) {
    throw new Error(`Failed to initialize item context: ${errorMessage || "unknown error"}`);
  }

  return (
    <>
      <BodyGlobalStyle />
      <CurrentMondayContext.Provider value={currentContext}>{children}</CurrentMondayContext.Provider>
    </>
  );
}

export function useCurrentMondayContext() {
  const context = useContext(CurrentMondayContext);
  if (!context) {
    throw new Error("Context not initialized");
  }
  return context;
}
